import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type ComplaintCategory,
  type ComplaintSubCategory,
  type ComplaintCategoryInput,
  type ComplaintSubCategoryInput,
} from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column } from '@/components/ui';
import { IconFlag } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 50;

/** Complaint Categories — the complaint taxonomy ("Driver / Payment / Vehicle /
 *  Safety" buckets and their sub-reasons) the rider & driver apps show when a
 *  customer files a complaint. content-svc DEFINES the `complaint_categories` +
 *  `complaint_sub_categories` tables (migration 027) and serves the apps' public
 *  read of the tree; admin-svc reads the full taxonomy (all statuses) through the
 *  shared Postgres pool.
 *
 *  READ + AUTHORED here: staff with cms.manage (ops + superadmin — the same
 *  content-authoring permission as Zones / Trip Types / the CMS) can create /
 *  edit / delete a category AND its sub-reasons. content-svc has no internal
 *  admin write endpoint for these tables (its authoring surface is faqs /
 *  policies / app-settings / contact only), so — exactly like the booking_statuses
 *  lookup — admin-svc owns the writes and performs them directly against the
 *  shared Postgres, RBAC-gated + audited server-side. Read-only roles still see
 *  the list. Covers the legacy Filament ComplaintCategoryResource +
 *  ComplaintSubCategoryResource (list + authoring).
 *
 *  The section is DEFENSIVE: it shows a clean "not yet available" state when the
 *  complaint_categories table isn't in this Postgres yet (migration not applied);
 *  the writes likewise degrade to a clean 503 (surfaced as an inline error) until
 *  the tables land. */
export function ComplaintCategories() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  // The drawer target: a ComplaintCategory to view/edit, 'new' to create, or null.
  const [editing, setEditing] = useState<ComplaintCategory | 'new' | null>(null);
  const canManage = useAdminPermission('cms.manage');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'complaint-categories', q, offset],
    queryFn: ({ signal }) =>
      adminApi.complaintCategories({ q: q || undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;
  const rows = data?.items ?? [];
  const activeCount = rows.filter((c) => c.status === 1).length;
  const subTotal = rows.reduce((n, c) => n + (c.sub_count ?? 0), 0);

  const columns: Array<Column<ComplaintCategory>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    {
      key: 'name',
      header: 'Category',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-100">{r.complaint_category_name || '—'}</div>
        </div>
      ),
    },
    {
      key: 'sub_count',
      header: 'Sub-categories',
      align: 'right',
      cell: (r) => (
        <Badge tone={r.sub_count > 0 ? 'info' : 'neutral'}>
          {r.sub_count} {r.sub_count === 1 ? 'reason' : 'reasons'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 1 ? 'ok' : 'neutral'} dot>
          {r.status === 1 ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setEditing(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          {canManage ? 'Manage' : 'View'}
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Complaint Categories"
        subtitle="The complaint taxonomy the apps show when a rider or driver files a complaint (defined by content-svc)."
        right={
          <div className="flex items-center gap-2">
            {canManage ? null : <Badge tone="neutral">read-only</Badge>}
            <SearchBox
              value={q}
              onChange={(v) => {
                setQ(v);
                setOffset(0);
              }}
              placeholder="Search categories…"
            />
          </div>
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Complaint categories not yet in Postgres"
            description={
              data?.note ??
              'The complaint_categories table is defined by content-svc and is not in this Postgres yet.'
            }
            icon={<IconFlag className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile label="Categories" value={data?.total ?? 0} hint="all statuses" icon={<IconFlag className="h-4 w-4" />} />
            <StatTile label="Active" value={activeCount} hint="shown in the apps" tone="ok" />
            <StatTile label="Sub-reasons" value={subTotal} hint="on this page" />
          </div>

          {canManage ? (
            <div className="flex justify-end">
              <button type="button" onClick={() => setEditing('new')} className="btn-primary">
                New category
              </button>
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading complaint categories…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load complaint categories" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<ComplaintCategory>
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No complaint categories"
                      description="No complaint_categories rows match this search."
                      icon={<IconFlag className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </div>
      )}

      {editing ? (
        <ComplaintCategoryDrawer
          category={editing === 'new' ? null : editing}
          canManage={canManage}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </AdminPage>
  );
}

// ─── category drawer (view / create / edit + sub-category authoring) ─────────

/** The category drawer. category=null ⇒ create form; otherwise it shows the
 *  category fields (editable for cms.manage) PLUS its nested sub-categories with
 *  per-row add / edit / delete (also cms.manage). Read-only roles get the same
 *  read view they had before. On a successful category create/edit/delete it
 *  invalidates the list and closes; sub-category writes invalidate the by-id
 *  detail so the list inside the drawer refreshes in place.
 *
 *  RBAC: only ops + superadmin hold cms.manage; the page only opens the create
 *  affordance for them and passes canManage through. admin-svc enforces the
 *  matrix server-side and 403s regardless — the gate here is a UX nicety. */
function ComplaintCategoryDrawer({
  category,
  canManage,
  onClose,
}: {
  category: ComplaintCategory | null;
  canManage: boolean;
  onClose: () => void;
}) {
  const isCreate = category === null;
  const qc = useQueryClient();

  // On create there's no id yet, so skip the detail fetch; on view/edit fetch the
  // category WITH its sub-categories (the list row carries only a sub-count).
  const detail = useQuery({
    queryKey: ['admin', 'complaint-category', category?.id],
    queryFn: ({ signal }) => adminApi.complaintCategory(category!.id, signal),
    enabled: !isCreate,
  });

  // The category being edited inside the drawer (create form, or an existing row
  // the staffer opened "Edit" on). null when just viewing the detail.
  const [editingCategory, setEditingCategory] = useState<boolean>(isCreate);
  // The sub-category drawer target: a row to edit, 'new', or null.
  const [editingSub, setEditingSub] = useState<ComplaintSubCategory | 'new' | null>(null);

  const c = detail.data?.category ?? category ?? null;
  const subs = detail.data?.category?.sub_categories ?? [];

  const refreshList = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'complaint-categories'] });
  };
  const refreshDetail = () => {
    if (category) qc.invalidateQueries({ queryKey: ['admin', 'complaint-category', category.id] });
  };

  const del = useMutation({
    mutationFn: () => adminApi.deleteComplaintCategory(category!.id),
    onSuccess: () => {
      refreshList();
      onClose();
    },
  });

  const title = isCreate
    ? 'New complaint category'
    : c?.complaint_category_name || `Complaint category #${c?.id ?? ''}`;
  const subtitle = isCreate
    ? 'Define a top-level complaint bucket'
    : `#${c?.id ?? ''} · ${c?.status === 1 ? 'Active' : 'Inactive'}`;

  return (
    <Drawer title={title} subtitle={subtitle} onClose={onClose}>
      <div className="space-y-4">
        {/* Category fields: an editable form (create, or "Edit" on an existing
            row) OR the read-only Field rows. */}
        {editingCategory ? (
          <CategoryForm
            category={isCreate ? null : c}
            onCancel={isCreate ? onClose : () => setEditingCategory(false)}
            onSaved={() => {
              refreshList();
              refreshDetail();
              if (isCreate) onClose();
              else setEditingCategory(false);
            }}
          />
        ) : (
          <Card>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted">Category</div>
              {canManage ? (
                <button
                  type="button"
                  onClick={() => setEditingCategory(true)}
                  className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
                >
                  Edit
                </button>
              ) : null}
            </div>
            <Field label="ID" value={`#${c?.id ?? ''}`} />
            <Field label="Name" value={c?.complaint_category_name || '—'} />
            <Field
              label="Status"
              value={
                <Badge tone={c?.status === 1 ? 'ok' : 'neutral'} dot>
                  {c?.status === 1 ? 'Active' : 'Inactive'}
                </Badge>
              }
            />
          </Card>
        )}

        {/* Sub-categories — only meaningful once the category exists (i.e. not in
            the create flow, where there's no parent id yet). */}
        {!isCreate ? (
          <Card flush>
            <div className="flex items-center justify-between px-4 pt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted">Sub-categories</div>
              <div className="flex items-center gap-2">
                <Badge tone="neutral">{detail.data ? subs.length : c?.sub_count ?? 0} total</Badge>
                {canManage ? (
                  <button
                    type="button"
                    onClick={() => setEditingSub('new')}
                    className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
                  >
                    Add
                  </button>
                ) : null}
              </div>
            </div>
            {detail.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner label="Loading sub-categories…" />
              </div>
            ) : detail.isError ? (
              <div className="px-4 pb-4 pt-2">
                <EmptyState title="Couldn't load sub-categories" description={(detail.error as Error)?.message} />
              </div>
            ) : subs.length === 0 ? (
              <div className="px-4 pb-4 pt-2">
                <EmptyState
                  title="No sub-categories"
                  description="This category has no sub-reasons defined."
                  icon={<IconFlag className="h-6 w-6" />}
                />
              </div>
            ) : (
              <ul className="divide-y divide-ink-800">
                {subs.map((s) => (
                  <li key={s.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="metric text-[11px] text-muted">#{s.id}</span>
                          <span className="truncate font-medium text-slate-100">
                            {s.complaint_sub_category_name || '—'}
                          </span>
                        </div>
                        {s.short_description ? (
                          <p className="mt-0.5 text-xs text-slate-300">{s.short_description}</p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge tone={s.status === 1 ? 'ok' : 'neutral'} dot>
                          {s.status === 1 ? 'Active' : 'Inactive'}
                        </Badge>
                        {canManage ? (
                          <button
                            type="button"
                            onClick={() => setEditingSub(s)}
                            className="rounded-md border border-ink-700 px-2 py-1 text-[11px] text-brand-400 hover:bg-ink-800"
                          >
                            Edit
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {/* Delete the whole category (cascades sub-reasons) — only on an existing
            row, only for cms.manage, and only when not mid-edit. */}
        {!isCreate && canManage && !editingCategory ? (
          <div className="flex items-center">
            <button
              type="button"
              disabled={del.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Delete this category? Its sub-categories are deleted too. This cannot be undone.',
                  )
                )
                  del.mutate();
              }}
              className="ml-auto rounded-md border border-danger/40 px-3 py-1.5 text-xs text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              {del.isPending ? 'Deleting…' : 'Delete category'}
            </button>
          </div>
        ) : null}
        {del.isError ? <p className="text-xs text-danger">{(del.error as Error)?.message}</p> : null}

        {!canManage ? (
          <div className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-850 px-3 py-2 text-xs text-muted">
            <IconFlag className="h-4 w-4 shrink-0" />
            Read-only. Editing the taxonomy needs the cms.manage permission (ops / superadmin).
          </div>
        ) : null}
      </div>

      {/* Sub-category authoring drawer (stacks over the category drawer). */}
      {editingSub && category ? (
        <SubCategoryDrawer
          parentId={category.id}
          sub={editingSub === 'new' ? null : editingSub}
          onClose={() => setEditingSub(null)}
          onSaved={() => {
            refreshDetail();
            refreshList();
            setEditingSub(null);
          }}
        />
      ) : null}
    </Drawer>
  );
}

// ─── category create/edit form ───────────────────────────────────────────────

/** The editable category form state. status is the string the <select> binds to. */
interface CategoryFormState {
  complaint_category_name: string;
  status: string; // '1' | '0'
}

function CategoryForm({
  category,
  onCancel,
  onSaved,
}: {
  category: ComplaintCategory | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = category !== null;
  const [form, setForm] = useState<CategoryFormState>(() => ({
    complaint_category_name: category?.complaint_category_name ?? '',
    status: String(category?.status ?? 1),
  }));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: ComplaintCategoryInput) =>
      isEdit ? adminApi.updateComplaintCategory(category!.id, input) : adminApi.createComplaintCategory(input),
    onSuccess: onSaved,
  });

  const onSubmit = () => {
    setClientError(null);
    const name = form.complaint_category_name.trim();
    if (!name) {
      setClientError('Category name is required.');
      return;
    }
    mutation.mutate({
      complaint_category_name: name,
      status: form.status === '1' ? 1 : 0,
    });
  };

  return (
    <Card>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
        {isEdit ? 'Edit category' : 'New category'}
      </div>
      <div className="space-y-3">
        <LabeledInput
          label="Category name"
          value={form.complaint_category_name}
          onChange={(v) => setForm((f) => ({ ...f, complaint_category_name: v }))}
          placeholder="e.g. Payment"
        />
        <StatusSelect
          value={form.status}
          onChange={(v) => setForm((f) => ({ ...f, status: v }))}
        />
        <div className="flex items-center gap-3 pt-1">
          <button type="button" disabled={mutation.isPending} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create category'}
          </button>
          <button type="button" disabled={mutation.isPending} onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
      </div>
    </Card>
  );
}

// ─── sub-category create/edit drawer ─────────────────────────────────────────

interface SubFormState {
  complaint_sub_category_name: string;
  short_description: string;
  status: string; // '1' | '0'
}

/** SubCategoryDrawer — create a sub-reason under parentId (sub=null) or edit an
 *  existing one. A sub-reason's parent is immutable (to re-parent: delete +
 *  recreate), matching the server contract; the form never offers a parent
 *  picker. On success it calls onSaved (which refreshes the detail + list). */
function SubCategoryDrawer({
  parentId,
  sub,
  onClose,
  onSaved,
}: {
  parentId: number;
  sub: ComplaintSubCategory | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = sub !== null;
  const [form, setForm] = useState<SubFormState>(() => ({
    complaint_sub_category_name: sub?.complaint_sub_category_name ?? '',
    short_description: sub?.short_description ?? '',
    status: String(sub?.status ?? 1),
  }));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: ComplaintSubCategoryInput) =>
      isEdit
        ? adminApi.updateComplaintSubCategory(sub!.id, input)
        : adminApi.createComplaintSubCategory(parentId, input),
    onSuccess: onSaved,
  });

  const del = useMutation({
    mutationFn: () => adminApi.deleteComplaintSubCategory(sub!.id),
    onSuccess: onSaved,
  });

  const onSubmit = () => {
    setClientError(null);
    const name = form.complaint_sub_category_name.trim();
    if (!name) {
      setClientError('Sub-category name is required.');
      return;
    }
    mutation.mutate({
      complaint_sub_category_name: name,
      short_description: form.short_description.trim(),
      status: form.status === '1' ? 1 : 0,
    });
  };

  const busy = mutation.isPending || del.isPending;

  return (
    <Drawer
      title={isEdit ? `Edit sub-category #${sub!.id}` : 'New sub-category'}
      subtitle={isEdit ? sub!.complaint_sub_category_name : 'Add a sub-reason to this category'}
      onClose={onClose}
    >
      <div className="space-y-4">
        <Card>
          <div className="space-y-3">
            <LabeledInput
              label="Sub-category name"
              value={form.complaint_sub_category_name}
              onChange={(v) => setForm((f) => ({ ...f, complaint_sub_category_name: v }))}
              placeholder="e.g. Overcharged"
            />
            <div>
              <label className="block text-[11px] text-muted">Short description</label>
              <textarea
                value={form.short_description}
                onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))}
                rows={2}
                placeholder="Optional one-line explanation shown to the rider/driver."
                className="mt-1 input text-sm"
              />
            </div>
            <StatusSelect value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} />
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={busy} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create sub-category'}
          </button>
          <button type="button" disabled={busy} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {isEdit ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Delete this sub-category? This cannot be undone.')) del.mutate();
              }}
              className="ml-auto rounded-md border border-danger/40 px-3 py-1.5 text-xs text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          ) : null}
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
        {del.isError ? <p className="text-xs text-danger">{(del.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

// ─── small shared inputs (match the locked light theme: .input + .btn-*) ─────

function LabeledInput({
  label,
  hint,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 input"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

function StatusSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[11px] text-muted">Status</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 input">
        <option value="1">Active</option>
        <option value="0">Inactive</option>
      </select>
    </div>
  );
}
